import { CakeTopologyBidirectionalDirection, CakeTopologyStateSnapshot } from '../models/ef-cake-topology.model';
import { EfModelParameterDefinition } from '../models/ef-model-parameters.model';
import { Constants } from './constants';

export abstract class EFateConstants {
  public static readonly MAX_NODE_NAME_LENGTH = 12;
  public static readonly NODE_LABEL_MAX_VISIBLE_LENGTH = 6;
  public static readonly MAX_GROUP_NAME_LENGTH = 25;
  public static readonly MAX_CAKE_GROUPS = 5;

  public static readonly GROUP_KEY_PREFIX = 'group-';
  public static readonly DEFAULT_GROUP_KEY: string = 'group-1';
  public static readonly DEFAULT_TOPOLOGY_KEY = 'default-topology';
  public static readonly DEFAULT_MODEL_ID = 'ef-model-001';
  public static readonly DEFAULT_GEOGRAPHY = 'CAKE';
  public static readonly DEFAULT_GROUP_NAME_PREFIX = 'Group';
  public static readonly PAYLOAD_LOG_LABEL = 'EF Cake Topology Save Payload';

  public static readonly GROUP_COLORS = ['#7ad2de', '#f4b3b3', '#bfecc0', '#d9c7f8', '#f7d3a4'];
  public static readonly TOPOLOGY_NODE_KEYS: string[] = ['Parent', 'A1', 'A2', 'A3', 'B1', 'B2', 'C1'];

  public static readonly DATA_PERCENT_DECIMALS = 2;
  public static readonly MAX_DATA_SET_NAME_LENGTH = 200;
  public static readonly MAX_STUDY_OPTIONS_NAME_LENGTH = 200;
  public static readonly MAX_STUDY_OPTIONS_DESCRIPTION_LENGTH = 500;
  public static readonly DEFAULT_STUDY_OPTIONS_NAME = 'Test Data';
  public static readonly DEFAULT_STUDY_OPTIONS_DESCRIPTION = 'A collection of test data sets';
  public static readonly DEFAULT_TIME_UNIT = 'days';
  public static readonly DEFAULT_CONCENTRATION_UNIT = '%';
  public static readonly TIME_HEADER_LABEL = 'Time';
  public static readonly formatUnitHeader = (label: string, unit: string): string => `${label} (${unit})`;
  public static readonly DATA_VISIBLE_ROWS = 9;
  public static readonly DATA_ROW_HEIGHT = 45;
  public static readonly DATA_HEADER_HEIGHT = 30;

  public static readonly NODE_KEY_TO_PERCENT_FIELD: Record<string, string> = {
    Parent: 'parentPercent',
    A1: 'a1Percent',
    A2: 'a2Percent',
    A3: 'a3Percent',
    B1: 'b1Percent',
    B2: 'b2Percent',
    C1: 'c1Percent',
  };

  public static readonly TIME_UNIT_OPTIONS: { label: string; value: string }[] = [
    { label: 'days', value: 'days' },
    { label: 'weeks', value: 'weeks' },
    { label: 'hours', value: 'hours' },
  ];

  public static readonly CONCENTRATION_UNIT_OPTIONS: { label: string; value: string }[] = [
    { label: '%', value: '%' },
    { label: '‰', value: '‰' },
    { label: 'ppm', value: 'ppm' },
    { label: 'ppb', value: 'ppb' },
    { label: 'mg/kg', value: 'mg/kg' },
    { label: 'g/ha', value: 'g/ha' },
  ];

  public static readonly CAKE_STUDY_TYPES: string[] = [
    'Aerobic Soil',
    'Aerobic Aquatic (Water/Sediment)',
    'Anaerobic Soil',
    'Anaerobic Aquatic (Water/Sediment)',
    'Aqueous Photolysis',
    'Flooded Aerobic Soil',
    'Hydrolysis',
    'Soil Photolysis',
  ];

  public static readonly DEFAULT_NODE_NAMES = {
    Parent: 'Parent',
    A1: 'A1',
    A2: 'A2',
    A3: 'A3',
    B1: 'B1',
    B2: 'B2',
    C1: 'C1',
  };

  public static readonly DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS: Record<string, CakeTopologyBidirectionalDirection> = {
    'a1-b1': 'none',
    'b1-c1': 'none',
    'a2-b2': 'none',
  };

  public static readonly DEFAULT_ACTIVE_STATUS_NODES: string[] = ['status-parent'];

  public static readonly EDGE_TO_SOURCE_NODE: Record<string, string> = {
    'parent-a1': 'Parent',
    'parent-b1': 'Parent',
    'parent-c1': 'Parent',
    'a1-a2': 'A1',
    'a2-a3': 'A2',
    'b1-b2': 'B1',
    'b1-a2': 'B1',
  };

  public static readonly EDGE_TO_TARGET_NODE: Record<string, string> = {
    'parent-a1': 'A1',
    'parent-b1': 'B1',
    'parent-c1': 'C1',
    'a1-a2': 'A2',
    'a2-a3': 'A3',
    'b1-b2': 'B2',
    'b1-a2': 'A2',
  };

  public static readonly BIDIRECTIONAL_EDGE_NODES: Record<string, { from: string; to: string }> = {
    'a1-b1': { from: 'A1', to: 'B1' },
    'b1-c1': { from: 'B1', to: 'C1' },
    'a2-b2': { from: 'A2', to: 'B2' },
  };

  public static readonly STATUS_NODE_TO_OWNER_NODE: Record<string, string> = {
    'status-parent': 'Parent',
    'status-a1': 'A1',
    'status-b1': 'B1',
    'status-c1': 'C1',
    'status-a2': 'A2',
    'status-b2': 'B2',
    'status-a3': 'A3',
  };

  public static createDefaultSnapshot(): CakeTopologyStateSnapshot {
    return {
      activeEdges: [],
      bidirectionalEdges: { ...EFateConstants.DEFAULT_BIDIRECTIONAL_EDGE_DIRECTIONS },
      activeStatusNodes: [...EFateConstants.DEFAULT_ACTIVE_STATUS_NODES],
    };
  }

  public static readonly REPORTS_GRID = {
    DATASET_HEADER: 'Dataset',
    PARENT_MODEL_HEADER: 'Parent Model',
    EXTRA_SOLVER_HEADER: 'Extra Solver',
    EXTRA_SOLVER_DEFAULT_VALUE: 'False',
    EXTRA_SOLVER_USED_VALUE: 'True',
    RUN_BUTTON_LABEL: 'Re-run',
    PREVIEW_ICON_CLASS: 'pi pi-search ef-reports-preview-icon',
    PREVIEW_TOOLTIP: 'View report',
    PREVIEW_COLUMN_WIDTH: 50,
    DOWNLOAD_BUTTON_LABEL: 'Download',
    PREVIEW_TITLE: 'Fit Report',
    REPORT_NOT_FOUND_TEXT: 'The report is not available for this dataset.',
    REPORT_FILE_EXTENSION: '.rtf',
    RUN_STATUS_LABEL: 'Run Status:',
    RERUN_CONFIRM_TEXT: 'The selected datasets will be rerun, the previous data will be overwritten, do you want to continue?',
    CONFIRM_BUTTON_TEXT: 'Yes',
    CANCEL_BUTTON_TEXT: 'No',
    CONFIRM_BUTTON_COLOR: '#0069be',
    CHI_SQUARED_HEADER: 'Chi-squared',
    RATE_PARAMETERS_HEADER: 'Rate Parameters',
    DT50_HEADER: 'DT50',
    DT90_HEADER: 'DT90',
    REPRESENTATIVE_DT50_HEADER: 'Representative DT50',
    TIME_NOTE_LABEL: 'Time note:',
    TIME_UNIT_MARKERS: { days: '*', weeks: '†', hours: '‡' } as Record<string, string>,
    MARKER_SEPARATOR: ' ',
    RATE_LABEL: 'k',
    P_VALUE_THRESHOLD: 0.01,
    P_VALUE_THRESHOLD_LABEL: 'p < 0.01',
    P_VALUE_LABEL_PREFIX: 'p = ',
    P_VALUE_NOT_DETERMINED_LABEL: 'p = n.d.',
    NOT_AVAILABLE_VALUE: 'N/A',
    DISSIPATION_TIME_MAX: 10000,
    DISSIPATION_TIME_MAX_LABEL: '>10,000',
    VALUE_SEPARATOR: ', ',
    SIGNIFICANT_DIGITS: 4,
    NO_VALUE_BACKGROUND: '#e6e6e6',
    HEADER_CLASS_PREFIX: 'ef-reports-header-',
    DATASET_COLUMN_WIDTH: 220,
    MODEL_COLUMN_WIDTH: 105,
    EXTRA_SOLVER_COLUMN_WIDTH: 95,
    CHI_SQUARED_COLUMN_WIDTH: 85,
    RATE_COLUMN_WIDTH: 120,
    DT_COLUMN_WIDTH: 85,
    REPRESENTATIVE_COLUMN_WIDTH: 125,
    MIN_COLUMN_WIDTH: 70,
    HEADER_HEIGHT: 52,
    GROUP_HEADER_HEIGHT: 40,
    ROW_HEIGHT: 40,
  };

  public static readonly EXTRA_SOLVER_LABEL = 'Use Extra Solver?';
  public static readonly EXTRA_SOLVER_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'Never', label: 'Never' },
    { value: 'IfRequired', label: 'If required' },
    { value: 'Always', label: 'Always (where possible)' },
  ];
  public static readonly DEFAULT_EXTRA_SOLVER_OPTION = 'IfRequired';

  public static readonly MODELS_TO_RUN_LABEL = 'Parameters for parent model:';
  public static readonly MODEL_OPTIONS_BY_GEOGRAPHY: Readonly<Record<string, ReadonlyArray<string>>> = {
    [Constants.CROP_GEOGRAPHIES.EUROPE_UNION]: ['SFO', 'FOMC', 'DFOP', 'HS'],
    [Constants.CROP_GEOGRAPHIES.USA]: ['SFO', 'DFOP', 'IORE'],
    [Constants.CROP_GEOGRAPHIES.CHINA]: ['SFO', 'FOMC', 'DFOP', 'HS'],
  };
  public static readonly DEFAULT_MODEL_OPTIONS: ReadonlyArray<string> = ['SFO', 'DFOP'];
  public static readonly DEFAULT_MODELS_TO_RUN: ReadonlyArray<string> = ['SFO'];
  public static readonly PARENT_COMPARTMENT_KEY = 'Parent';

  public static readonly MODEL_PARAMETERS = {
    DIALOG_TITLE: 'Set Parameters',
    PARENT_SECTION_TITLE: 'Parent Parameters',
    METABOLITE_SECTION_TITLE: 'Metabolite Parameters',
    METABOLITE_MODEL_LABEL: 'Model',
    AUTOMATIC_LABEL: 'Automatic',
    FLOW_LABEL_PREFIX: 'flow to',
    FLOW_KEY_PREFIX: 'f_to_',
    FLOW_LOWER_LIMIT: 0,
    FLOW_UPPER_LIMIT: 1,
    FLOW_DECIMALS: 6,
    MIN_LIMIT_GAP: 0.1,
    LIMIT_GAP_RATIO: 0.1,
    MAX_FRACTION_DIGITS: 6,
  };

  public static readonly PARENT_INITIAL_AMOUNT_PARAMETER: EfModelParameterDefinition = {
    key: 'M0', label: 'M0', initialValue: 100, lowerLimit: 0, upperLimit: null,
  };

  public static readonly METABOLITE_INITIAL_AMOUNT_PARAMETER: EfModelParameterDefinition = {
    key: 'M0', label: 'M0', initialValue: 0, lowerLimit: 0, upperLimit: null, isFixed: true,
  };

  public static readonly METABOLITE_MODEL_OPTIONS: ReadonlyArray<string> = ['SFO', 'DFOP'];
  public static readonly DEFAULT_METABOLITE_MODEL = 'SFO';
  public static readonly DFOP_MODEL = 'DFOP';
  // Only first-level metabolites can use DFOP, and only when no other metabolite feeds them.
  public static readonly PRIMARY_METABOLITE_KEYS: ReadonlyArray<string> = ['A1', 'B1', 'C1'];

  // CAKE defaults per model (CompModel.Clear); automatic ones are locked while CAKE's automatic options are on.
  public static readonly MODEL_PARAMETER_DEFINITIONS: Readonly<Record<string, ReadonlyArray<EfModelParameterDefinition>>> = {
    SFO: [
      { key: 'k', label: 'k', initialValue: 0.1, lowerLimit: 0, upperLimit: null },
    ],
    FOMC: [
      { key: 'alpha', label: 'alpha', initialValue: 0.1, lowerLimit: 0, upperLimit: null },
      { key: 'beta', label: 'beta', initialValue: 0.01, lowerLimit: 0, upperLimit: null },
    ],
    HS: [
      { key: 'k1', label: 'k1', initialValue: 0.1, lowerLimit: 0, upperLimit: null },
      { key: 'k2', label: 'k2', initialValue: 0.01, lowerLimit: 0, upperLimit: null },
      { key: 'tb', label: 'tb', initialValue: 10, lowerLimit: 0, upperLimit: null, isAutomatic: true },
    ],
    DFOP: [
      { key: 'k1', label: 'k1', initialValue: 0.1, lowerLimit: 0, upperLimit: null },
      { key: 'k2', label: 'k2', initialValue: 0.01, lowerLimit: 0, upperLimit: null },
      { key: 'g', label: 'g', initialValue: 0.5, lowerLimit: 0, upperLimit: 1 },
    ],
    IORE: [
      { key: 'k', label: 'k', initialValue: 0.1, lowerLimit: 0, upperLimit: null, isAutomatic: true },
      { key: 'N', label: 'N', initialValue: 2.15, lowerLimit: 0, upperLimit: 25, isAutomatic: true },
    ],
  };

  public static readonly BIDIRECTIONAL_EDGE_DIRECTIONS: {
    readonly NONE: CakeTopologyBidirectionalDirection;
    readonly FORWARD: CakeTopologyBidirectionalDirection;
    readonly REVERSE: CakeTopologyBidirectionalDirection;
  } = {
    NONE: 'none',
    FORWARD: 'forward',
    REVERSE: 'reverse',
  };
}
